import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, X } from "@phosphor-icons/react";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Floating banner shown ONCE on first visit, after `/api/locale/detect` picks a
 * non-English language for the user. Lets them either keep the new language or
 * roll back to English. Dismisses on either click and never shows again
 * (gated by `clariva_locale_detected` localStorage flag set by the provider).
 */
export default function LocaleDetectedBanner() {
  const { autoDetected, dismissBanner, changeLang, t, SUPPORTED_LANGS } = useI18n();
  if (!autoDetected) return null;

  const langName =
    SUPPORTED_LANGS.find((l) => l.code === autoDetected.to)?.native || autoDetected.to;

  const undo = async () => {
    await changeLang("en");
    dismissBanner();
  };

  return (
    <AnimatePresence>
      <motion.div
        key="locale-banner"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.35 }}
        className="fixed bottom-4 right-4 z-[80] max-w-sm rounded-xl border border-[var(--border)] bg-[var(--surface-1)] shadow-xl backdrop-blur p-4 flex items-start gap-3"
        data-testid="locale-banner"
      >
        <div className="shrink-0 w-9 h-9 rounded-full bg-[var(--accent)]/15 text-[var(--accent)] flex items-center justify-center">
          <Globe size={18} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[var(--text-1)] leading-relaxed">
            {t("locale.banner.detected", { lang: langName })}
          </p>
          <div className="mt-2.5 flex items-center gap-3">
            <button
              type="button"
              onClick={dismissBanner}
              className="text-xs font-medium text-[var(--accent)] hover:underline"
              data-testid="locale-banner-keep-btn"
            >
              {t("locale.banner.keep", { lang: langName })}
            </button>
            <span className="text-xs text-[var(--text-3)]">·</span>
            <button
              type="button"
              onClick={undo}
              className="text-xs font-medium text-[var(--text-2)] hover:text-[var(--text-1)] hover:underline"
              data-testid="locale-banner-undo-btn"
            >
              {t("locale.banner.undo")}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismissBanner}
          aria-label="Close"
          className="text-[var(--text-3)] hover:text-[var(--text-1)] p-0.5"
          data-testid="locale-banner-close-btn"
        >
          <X size={14} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
